/**
 * Accessibility Utilities
 * 
 * Centralized aria labels, live region helpers, and focus management utilities.
 * Ensures consistent accessibility patterns across the application.
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Focus management utilities
 */
export const focusUtils = {
  /**
   * Trap focus within an element
   */
  trapFocus: (element: HTMLElement): (() => void) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Restore focus to a previously focused element
   */
  restoreFocus: (element: HTMLElement | null): void => {
    if (element && typeof element.focus === 'function') {
      element.focus();
    }
  },

  /**
   * Get the currently focused element
   */
  getCurrentFocus: (): HTMLElement | null => {
    return document.activeElement as HTMLElement;
  },

  /**
   * Check if an element is focusable
   */
  isFocusable: (element: HTMLElement): boolean => {
    const tabIndex = element.getAttribute('tabindex');
    if (tabIndex === '-1') return false;
    
    const tagName = element.tagName.toLowerCase();
    const focusableTags = ['button', 'input', 'select', 'textarea', 'a'];
    
    if (focusableTags.includes(tagName)) return true;
    if (tabIndex !== null) return true;
    
    return false;
  },
};

/**
 * Live region utilities for screen readers
 */
export const liveRegionUtils = {
  /**
   * Create a live region element
   */
  createLiveRegion: (): HTMLElement => {
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    document.body.appendChild(liveRegion);
    return liveRegion;
  },

  /**
   * Announce a message to screen readers
   */
  announce: (message: string, priority: 'polite' | 'assertive' = 'polite'): void => {
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.textContent = message;
    
    document.body.appendChild(liveRegion);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(liveRegion);
    }, 1000);
  },

  /**
   * Create a persistent live region
   */
  createPersistentLiveRegion: (id: string): HTMLElement => {
    let liveRegion = document.getElementById(id) as HTMLElement;
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = id;
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }
    
    return liveRegion;
  },
};

/**
 * ARIA label utilities
 */
export const ariaUtils = {
  /**
   * Generate descriptive labels for common UI patterns
   */
  labels: {
    closeModal: (context?: string): string => 
      context ? `Close ${context} modal` : 'Close modal',
    
    openModal: (context?: string): string => 
      context ? `Open ${context} modal` : 'Open modal',
    
    nextItem: (current: number, total: number, context?: string): string => 
      context ? `Go to next ${context} (${current + 1} of ${total})` : `Next item (${current + 1} of ${total})`,
    
    previousItem: (current: number, total: number, context?: string): string => 
      context ? `Go to previous ${context} (${current - 1} of ${total})` : `Previous item (${current - 1} of ${total})`,
    
    downloadFile: (filename: string): string => `Download ${filename}`,
    
    zoomIn: (currentZoom?: number): string => 
      currentZoom ? `Zoom in (currently ${Math.round(currentZoom * 100)}%)` : 'Zoom in',
    
    zoomOut: (currentZoom?: number): string => 
      currentZoom ? `Zoom out (currently ${Math.round(currentZoom * 100)}%)` : 'Zoom out',
    
    resetView: (): string => 'Reset view to original size and position',
    
    toggleSlideshow: (isPlaying: boolean): string => 
      isPlaying ? 'Pause slideshow' : 'Start slideshow',
    
    clearLogs: (): string => 'Clear console logs',
    
    copyLogs: (): string => 'Copy console logs to clipboard',
    
    progressStatus: (status: string, progress?: number): string => 
      progress ? `${status} (${progress}% complete)` : status,
  },

  /**
   * Generate descriptions for complex UI elements
   */
  descriptions: {
    modal: (title: string, description?: string): string => 
      description ? `${title}. ${description}` : title,
    
    progressBar: (current: number, total: number, unit: string = 'items'): string => 
      `Progress: ${current} of ${total} ${unit}`,
    
    console: (logCount: number, mode: 'summary' | 'raw'): string => 
      `Console with ${logCount} ${mode} log entries`,
    
    downloader: (type: string, status: string): string => 
      `${type} downloader: ${status}`,
  },
};

/**
 * Hook for managing focus trap
 */
export function useFocusTrap(isActive: boolean = true) {
  const containerRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const trapFocus = useCallback(() => {
    if (!containerRef.current || !isActive) return;

    // Store current focus
    previousFocusRef.current = focusUtils.getCurrentFocus();

    // Trap focus
    return focusUtils.trapFocus(containerRef.current);
  }, [isActive]);

  const restoreFocus = useCallback(() => {
    if (previousFocusRef.current) {
      focusUtils.restoreFocus(previousFocusRef.current);
      previousFocusRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const cleanup = trapFocus();
    return () => {
      cleanup?.();
      restoreFocus();
    };
  }, [isActive, trapFocus, restoreFocus]);

  return { containerRef, trapFocus, restoreFocus };
}

/**
 * Hook for managing live regions
 */
export function useLiveRegion(id?: string) {
  const liveRegionRef = useRef<HTMLElement | null>(null);

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (id) {
      const liveRegion = liveRegionUtils.createPersistentLiveRegion(id);
      liveRegion.textContent = message;
      liveRegionRef.current = liveRegion;
    } else {
      liveRegionUtils.announce(message, priority);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      liveRegionRef.current = liveRegionUtils.createPersistentLiveRegion(id);
    }

    return () => {
      if (liveRegionRef.current && liveRegionRef.current.parentNode) {
        liveRegionRef.current.parentNode.removeChild(liveRegionRef.current);
      }
    };
  }, [id]);

  return { announce };
}

/**
 * Hook for managing accessible announcements
 */
export function useAccessibleAnnouncements() {
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    liveRegionUtils.announce(message, priority);
  }, []);

  const announceError = useCallback((error: string) => {
    announce(`Error: ${error}`, 'assertive');
  }, [announce]);

  const announceSuccess = useCallback((message: string) => {
    announce(`Success: ${message}`, 'polite');
  }, [announce]);

  const announceProgress = useCallback((message: string) => {
    announce(message, 'polite');
  }, [announce]);

  return {
    announce,
    announceError,
    announceSuccess,
    announceProgress,
  };
}

/**
 * Utility for generating accessible IDs
 */
export const idUtils = {
  /**
   * Generate a unique ID for accessibility
   */
  generateId: (prefix: string = 'accessibility'): string => {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Create ID relationships for accessibility
   */
  createRelationship: (labelId: string, controlId: string): { labelId: string; controlId: string } => {
    return {
      labelId: labelId || idUtils.generateId('label'),
      controlId: controlId || idUtils.generateId('control'),
    };
  },
};

/**
 * Keyboard navigation utilities
 */
export const keyboardUtils = {
  /**
   * Common keyboard event handlers
   */
  handlers: {
    onEnter: (callback: () => void) => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        callback();
      }
    },

    onEscape: (callback: () => void) => (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        callback();
      }
    },

    onArrowKeys: (callbacks: {
      onUp?: () => void;
      onDown?: () => void;
      onLeft?: () => void;
      onRight?: () => void;
    }) => (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          callbacks.onUp?.();
          break;
        case 'ArrowDown':
          e.preventDefault();
          callbacks.onDown?.();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          callbacks.onLeft?.();
          break;
        case 'ArrowRight':
          e.preventDefault();
          callbacks.onRight?.();
          break;
      }
    },
  },

  /**
   * Check if a key combination is pressed
   */
  isKeyCombo: (e: KeyboardEvent, key: string, modifiers: string[] = []): boolean => {
    const keyMatches = e.key.toLowerCase() === key.toLowerCase();
    const ctrlMatches = modifiers.includes('ctrl') ? e.ctrlKey : !e.ctrlKey;
    const altMatches = modifiers.includes('alt') ? e.altKey : !e.altKey;
    const shiftMatches = modifiers.includes('shift') ? e.shiftKey : !e.shiftKey;
    const metaMatches = modifiers.includes('meta') ? e.metaKey : !e.metaKey;

    return keyMatches && ctrlMatches && altMatches && shiftMatches && metaMatches;
  },
};
